import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tooltip } from "primereact/tooltip";

import type { Permission } from "@/modules/permissions";
import styles from "./edit-permission-dialog.module.scss";

export const EDIT_PERMISSION_TITLE = "Editar permissão";
/** MS002 — apresentada ao posicionar o cursor sobre o campo Código desabilitado. */
export const CODE_LOCKED_MESSAGE = "Não é possível editar o código da permissão.";
export const FIELD_REQUIRED_MESSAGE = "Campo obrigatório";

/** Liga o botão Salvar, que vive no rodapé do Dialog, ao formulário do corpo. */
const FORM_ID = "edit-permission-form";

/** Limites da tabela `plataforma.permissao`. */
const NAME_MAX_LENGTH = 150;
const DESCRIPTION_MAX_LENGTH = 500;

export interface EditPermissionValues {
  nome: string;
  descricao: string;
}

interface FieldErrors {
  nome?: string;
  descricao?: string;
}

interface EditPermissionDialogProps {
  permission: Permission;
  /**
   * Persiste a edição. Cabe ao chamador comunicar o resultado e fechar o modal
   * no sucesso — em caso de falha o modal segue aberto com os dados digitados.
   * A promessa não deve ser rejeitada.
   */
  onSubmit: (values: EditPermissionValues) => Promise<void>;
  onCancel: () => void;
}

function getFieldErrors({ nome, descricao }: EditPermissionValues): FieldErrors {
  const errors: FieldErrors = {};

  if (!nome) errors.nome = FIELD_REQUIRED_MESSAGE;
  if (!descricao) errors.descricao = FIELD_REQUIRED_MESSAGE;

  return errors;
}

function inputClassName(base: string | undefined, invalid: boolean): string {
  return [base, invalid ? styles.inputInvalid : ""].filter(Boolean).join(" ");
}

export function EditPermissionDialog({
  permission,
  onSubmit,
  onCancel,
}: EditPermissionDialogProps) {
  const [nome, setNome] = useState(permission.nome);
  const [descricao, setDescricao] = useState(permission.descricao);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // RN01: só nome e descrição saem do formulário; o código nunca é enviado.
    const values: EditPermissionValues = { nome: nome.trim(), descricao: descricao.trim() };
    const nextErrors = getFieldErrors(values);
    setErrors(nextErrors);

    if (nextErrors.nome || nextErrors.descricao) return;

    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  }

  // O rodapé é uma faixa própria do Dialog, portanto fica fora do <form>:
  // o atributo `form` mantém Salvar como botão de envio do formulário.
  const footer = (
    <div className={styles.actions}>
      <Button
        type="button"
        className={styles.cancelButton}
        label="Cancelar"
        severity="secondary"
        text
        onClick={onCancel}
        disabled={isSubmitting}
        data-testid="edit-permission-cancel"
      />
      <Button
        type="submit"
        form={FORM_ID}
        className={styles.saveButton}
        label="Salvar"
        loading={isSubmitting}
        data-testid="edit-permission-save"
      />
    </div>
  );

  return (
    <Dialog
      className={styles.dialog}
      maskClassName={styles.mask}
      header={EDIT_PERMISSION_TITLE}
      footer={footer}
      visible
      modal
      draggable={false}
      resizable={false}
      ariaCloseIconLabel="Fechar"
      onHide={onCancel}
      breakpoints={{ "40rem": "95vw" }}
      data-testid="edit-permission-dialog"
    >
      <form id={FORM_ID} className={styles.form} onSubmit={handleSubmit} noValidate>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-permission-code">
            Código
          </label>
          {/* O input desabilitado não recebe eventos de mouse: o tooltip fica no invólucro. */}
          <span
            className={styles.codeField}
            data-pr-tooltip={CODE_LOCKED_MESSAGE}
            data-testid="edit-permission-code-field"
          >
            <InputText
              id="edit-permission-code"
              className={`${styles.input} ${styles.codeInput}`}
              value={permission.codigo}
              disabled
              aria-describedby="edit-permission-code-hint"
              data-testid="edit-permission-code"
            />
          </span>
          <small id="edit-permission-code-hint" className={styles.srOnly}>
            {CODE_LOCKED_MESSAGE}
          </small>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-permission-name">
            Nome <span aria-hidden>*</span>
          </label>
          <InputText
            id="edit-permission-name"
            className={inputClassName(styles.input, Boolean(errors.nome))}
            value={nome}
            maxLength={NAME_MAX_LENGTH}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setNome(event.currentTarget.value)}
            disabled={isSubmitting}
            required
            aria-invalid={Boolean(errors.nome)}
            aria-describedby={errors.nome ? "edit-permission-name-error" : undefined}
            data-testid="edit-permission-name"
          />
          {errors.nome ? (
            <span
              id="edit-permission-name-error"
              className={styles.fieldError}
              role="alert"
              data-testid="edit-permission-name-error"
            >
              {errors.nome}
            </span>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-permission-description">
            Descrição <span aria-hidden>*</span>
          </label>
          <InputTextarea
            id="edit-permission-description"
            className={inputClassName(styles.textarea, Boolean(errors.descricao))}
            value={descricao}
            maxLength={DESCRIPTION_MAX_LENGTH}
            rows={4}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setDescricao(event.currentTarget.value)
            }
            disabled={isSubmitting}
            required
            aria-invalid={Boolean(errors.descricao)}
            aria-describedby={errors.descricao ? "edit-permission-description-error" : undefined}
            data-testid="edit-permission-description"
          />
          {errors.descricao ? (
            <span
              id="edit-permission-description-error"
              className={styles.fieldError}
              role="alert"
              data-testid="edit-permission-description-error"
            >
              {errors.descricao}
            </span>
          ) : null}
        </div>
      </form>

      <Tooltip target={`.${styles.codeField}`} position="right" className={styles.tooltip} />
    </Dialog>
  );
}
